import React, { useEffect, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import { cloneDeep } from 'lodash'

// Components
import { InputNewMemberRow } from '../../inputs/InputNewMemberRow'
import { InputUploadMemberPictureRow } from '../../inputs/InputUploadMemberPictureRow'
import { InputCheckMemberInfo } from '../../inputs/InputCheckMemberInfo'
import MainModal, { IMainModalButton } from '../../modals/common/MainModal'
import { ModalBackButton } from '../../modals/components/ModalBackButton'

// Store
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectHorses,
  setHorses,
  setIsMyHorse,
  setSelectedHorseStatus,
} from '../../../store/horses/horseSlice'
import {
  riderProfileRegistrationActions,
  selectRiderProfileHorse,
  selectRiderProfileNewTeamRider,
  selectRiderProfileRider,
  selectRiderProfileTeam,
  selectRiderProfileType,
} from '../../../store/riderProfileRegistration/riderProfileRegistrationSlice'
import { selectProfileData, setProfileDetails } from '../../../store/user/userSlice'
import { selectTeamTabData, setTeamTabData } from '../../../store/registration/registrationSlice'

// Helpers
import useToasterHelper from '../../../helpers/ToasterHelper'
import { uploadPicture } from '../../../helpers/uploadPicture'
import { useCreate } from '../../../hooks/useCreate'
import { createHorseData } from '../../modals/common/createHorseData'
import { getConvertedData, getSelectedUserAsTeamMember } from '../../../models/interface.helper'

import FirestoreService from '../../../services/firestoreService'

// Modal
import { UserModel } from '../../../models/users/user.model'

// Types
import { IInputHorseData, IInputHorseDataKey } from '../types/inputData'
import { ECompetitorTypes } from '../../../types/competitor_types'

// Const
import { MODAL_CONSTS } from '../../../const/modal-const'
import { MESSAGES_CONST } from '../../../const/messages-const'
import { initialHorseInputs, inputHorseIcons } from './data/input-data'
import { CONST } from '../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

type AddNewHorseModalProps = {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    backHandler?: () => void
    onlyHorse?: boolean
  }
}

const userIcon = '/assets/img/HorsesSign.png'
const HORSE_PATH = CONST.DATA.STORAGE.HORSE.HORSE_PROFILE_IMAGE_URL.PREFIX

export const AddNewHorseModal = ({ handleModal, dataToPassOn }: AddNewHorseModalProps) => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()
  const { createNewTeamMember, getFirebaseErrors, updateUserTeamMembers } = useCreate()

  const [loading, setLoading] = useState(false)
  const [horsePicture, setHorsePicture] = useState<string>('')
  const [horsePictureFile, setHorsePictureFile] = useState<string>('')
  const [isValid, setIsValid] = useState(false)
  const [imgLoading, setImgLoading] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [inputData, setInputDate] = useState<IInputHorseData>(cloneDeep(initialHorseInputs))

  const myHorses = useAppSelector(selectHorses)
  const profileData = useAppSelector(selectProfileData)
  const teamTabData = useAppSelector(selectTeamTabData)

  const riderProfileType = useAppSelector(selectRiderProfileType)
  const riderProfileHorse = useAppSelector(selectRiderProfileHorse)
  const riderProfileTeam = useAppSelector(selectRiderProfileTeam)
  const riderProfileRider = useAppSelector(selectRiderProfileRider)
  const riderProfileNewTeamRider = useAppSelector(selectRiderProfileNewTeamRider)

  useEffect(() => {
    const checkValidity = () => {
      for (const key in inputData) {
        const field = inputData[key as IInputHorseDataKey]
        if (field.required && !field.value) {
          return false
        }
      }
      return true
    }

    setIsValid(isConfirmed && checkValidity())
  }, [inputData, isConfirmed])

  useEffect(() => {
    if (riderProfileHorse) {
      setInputDate(riderProfileHorse.inputData)
      setIsConfirmed(true)
    }
    if (riderProfileHorse?.horsePicture) {
      const imageUrl = URL.createObjectURL(riderProfileHorse.horsePicture)
      setHorsePicture(imageUrl)
    }
  }, [riderProfileHorse])

  const updateProfilePic = async (event: any) => {
    setImgLoading(true)

    const files = event.target.files
    const file = files[0]
    if (!file) return toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })
    try {
      if (riderProfileRider) {
        setHorsePictureFile(file)
        const imageUrl = URL.createObjectURL(file)
        setHorsePicture(imageUrl)
      } else {
        const downloadUrl = await uploadPicture(file, HORSE_PATH)
        if (downloadUrl) setHorsePicture(downloadUrl)
        toastFunctions.success({ message: MESSAGES_CONST.PIC_UPDATED })
      }
    } catch (error) {
      console.error(error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setImgLoading(false)
    }
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      if (riderProfileRider) {
        dispatch(
          riderProfileRegistrationActions.setHorse({
            horsePicture: horsePictureFile || riderProfileHorse?.horsePicture,
            inputData,
          })
        )

        handleModal(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)

        if (riderProfileType === ECompetitorTypes.teamRider) {
          handleModal(true, MODAL_CONSTS.REGISTER_TEAM, {
            userTeamMembers: riderProfileTeam,
            competitor: riderProfileRider,
          })
        } else if (riderProfileType === ECompetitorTypes.loneRider) {
          handleModal(true, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
        }
      } else {
        let userData: { id: string | null; email: string | null } | null

        if (riderProfileNewTeamRider) {
          userData = await createNewTeamMember({
            competitor: profileData,
            teamMember: {
              picture: riderProfileNewTeamRider?.picture,
              email: riderProfileNewTeamRider.inputData.email.value,
              userName: riderProfileNewTeamRider.inputData.userName.value,
              userFullName: riderProfileNewTeamRider.inputData.userFullName.value,
              date: riderProfileNewTeamRider.inputData.date.value,
              nationality: riderProfileNewTeamRider.inputData.nationality.value,
              discipline: riderProfileNewTeamRider.inputData.discipline.value,
            },
          })

          // Update user team members
          const userSnaps = await FirestoreService.getItem(
            COLLECTIONS.USERS.NAME,
            userData?.id ?? ''
          )
          const newTeamMemberUser = getConvertedData(
            UserModel.fromFirestoreDoc(userSnaps).toObject()
          )
          await updateUserTeamMembers([newTeamMemberUser], profileData)

          // Update users "Start" tab for registration on event
          const userAsTeamMember = getConvertedData(
            getSelectedUserAsTeamMember(newTeamMemberUser, profileData)
          )
          dispatch(setTeamTabData([...teamTabData, userAsTeamMember]))

          const updatedCompetitorSnaps = await FirestoreService.getItem(
            COLLECTIONS.USERS.NAME,
            profileData.id
          )
          const updatedCompetitor = UserModel.fromFirestoreDoc(updatedCompetitorSnaps).toObject()
          dispatch(setProfileDetails(updatedCompetitor))

          dispatch(riderProfileRegistrationActions.clear())
        } else {
          userData = {
            id: profileData.id,
            email: profileData.userEmail,
          }
        }

        if (!userData?.id || !userData?.email) {
          return null
        }

        const { horseToCreate } = await createHorseData({
          userData: { ...profileData, id: userData.id },
          horsePicture,
          inputData,
        })

        dispatch(setHorses([...myHorses, horseToCreate]))
        dispatch(setIsMyHorse(true))
        dispatch(setSelectedHorseStatus('fulfilled'))

        if (riderProfileRider || !dataToPassOn.onlyHorse) {
          toastFunctions.success({ message: MESSAGES_CONST.HORSE_TEAM_MEMBER_CREATED })
        } else {
          toastFunctions.success({ message: MESSAGES_CONST.HORSE_CREATED })
        }

        handleModal(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)
      }
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({ message: getFirebaseErrors(error) })
    } finally {
      setLoading(false)
    }
  }

  const onSubmitWithoutHorse = async () => {
    setLoading(true)
    try {
      if (profileData.id && riderProfileNewTeamRider) {
        const userData = await createNewTeamMember({
          competitor: profileData,
          teamMember: {
            picture: riderProfileNewTeamRider?.picture,
            email: riderProfileNewTeamRider.inputData.email.value,
            userName: riderProfileNewTeamRider.inputData.userName.value,
            userFullName: riderProfileNewTeamRider.inputData.userFullName.value,
            date: riderProfileNewTeamRider.inputData.date.value,
            nationality: riderProfileNewTeamRider.inputData.nationality.value,
            discipline: riderProfileNewTeamRider.inputData.discipline.value,
          },
        })

        // Update user team members
        const userSnaps = await FirestoreService.getItem(COLLECTIONS.USERS.NAME, userData?.id ?? '')
        const newTeamMemberUser = getConvertedData(UserModel.fromFirestoreDoc(userSnaps).toObject())
        await updateUserTeamMembers([newTeamMemberUser], profileData)

        // Update users "Start" tab for registration on event
        const userAsTeamMember = getConvertedData(
          getSelectedUserAsTeamMember(newTeamMemberUser, profileData)
        )
        dispatch(setTeamTabData([...teamTabData, userAsTeamMember]))

        const updatedCompetitorSnaps = await FirestoreService.getItem(
          COLLECTIONS.USERS.NAME,
          profileData.id
        )
        const updatedCompetitor = UserModel.fromFirestoreDoc(updatedCompetitorSnaps).toObject()
        dispatch(setProfileDetails(updatedCompetitor))

        dispatch(riderProfileRegistrationActions.clear())
      } else if (riderProfileType === ECompetitorTypes.teamRider) {
        handleModal(true, MODAL_CONSTS.REGISTER_TEAM, {
          userTeamMembers: riderProfileTeam,
          competitor: riderProfileRider,
        })
      } else if (riderProfileType === ECompetitorTypes.loneRider) {
        handleModal(true, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
      }

      dispatch(riderProfileRegistrationActions.setHorse(null))
      handleModal(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({ message: getFirebaseErrors(error) })
    } finally {
      setLoading(false)
    }
  }

  const onChangeHandler = (key: string, value: string) => {
    const newInputData = { ...inputData }
    if (newInputData[key as IInputHorseDataKey]) {
      newInputData[key as IInputHorseDataKey].value = value
      newInputData[key as IInputHorseDataKey].hasError = false
      setInputDate(newInputData)
    }
    return {}
  }

  const saveBtn: IMainModalButton = {
    label: riderProfileRider ? 'NEXT >' : 'SAVE',
    loading,
    onClick: onSubmit,
    disabled: !isValid,
    fullWidth: true,
  }

  const saveWithoutHorseBtn = {
    label: 'SAVE WITHOUT HORSE',
    loading,
    onClick: onSubmitWithoutHorse,
    fullWidth: true,
  }

  const cancelBtn: IMainModalButton = {
    label: 'CANCEL',
    loading,
    bgClass: 'bg-SeabiscuitLightThemeColor',
    className: 'outline-none !w-full',
    borderClass: 'border border-transparent',
    textClass: 'text-SeabiscuitLightTextColor',
    onClick: () => handleModal(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER),
  }

  const btns: IMainModalButton[] =
    riderProfileRider || !dataToPassOn.onlyHorse
      ? [saveBtn, saveWithoutHorseBtn, cancelBtn]
      : [saveBtn, cancelBtn]

  return (
    <MainModal
      title="Add new horse"
      description={'Create a profile for your horse'}
      show={true}
      goBackBtn={dataToPassOn.backHandler && <ModalBackButton onClick={dataToPassOn.backHandler} />}
      type="ADD_NEW_HORSE_MEMBER"
      size="lg"
      onTransitionEnd={() => null}
      titleClassName="!font-normal"
      buttons={btns}
    >
      <div className={'flex flex-col gap-4 mt-4 '}>
        <div className={'flex flex-col gap-2 border-b border-[#D3DAEE] pb-4'}>
          <InputUploadMemberPictureRow
            onchangeHandler={updateProfilePic}
            picture={horsePicture}
            isLoading={imgLoading}
            title={'Profile picture'}
            userIcon={userIcon}
          />
          {Object.keys(inputData).map((key) => (
            <InputNewMemberRow
              key={key}
              value={inputData[key as IInputHorseDataKey].value}
              onChangeHandler={onChangeHandler}
              placeholder={inputData[key as IInputHorseDataKey].placeholder}
              inputName={inputData[key as IInputHorseDataKey].name}
              name={inputData[key as IInputHorseDataKey].label}
              icon={
                inputHorseIcons[inputData[key as IInputHorseDataKey].name as IInputHorseDataKey]
              }
              isRequired={inputData[key as IInputHorseDataKey].required}
              type={inputData[key as IInputHorseDataKey].type}
              selectData={inputData[key as IInputHorseDataKey].selectData || []}
            />
          ))}
        </div>
        <InputCheckMemberInfo
          onClick={() => setIsConfirmed(!isConfirmed)}
          isChecked={isConfirmed}
          text={
            'By ticking this box I confirm that I have been given permission to create a horse on behalf of this person and register the horse for events.'
          }
        />
      </div>
    </MainModal>
  )
}
